import { createAction } from '@reduxjs/toolkit';
import { logger } from 'common/services';

import type {
  BroadcastChannelMessage,
  OpenNatterMessage,
  OtherUserJoinedRoom,
  OtherUserJoinedWaitingRoom,
  OtherUserLeftEvent,
  SelfJoinedWaitingRoom,
  SelfLeftEvent,
  SelfMatched,
  SelfNotification,
  SelfUnmatched,
  SequencedUserNotification,
} from '../types/messages';

/**
 * Redux actions for Open Natter WebSocket messages
 */

// Personal channel actions (sequenced)
export const selfJoinedWaitingRoomAction = createAction<SelfJoinedWaitingRoom>('openNatter:SelfJoinedWaitingRoom');
export const selfMatchedAction = createAction<SelfMatched>('openNatter:SelfMatched');
export const selfUnmatchedAction = createAction<SelfUnmatched>('openNatter:SelfUnmatched');
export const selfLeftEventAction = createAction<SelfLeftEvent>('openNatter:SelfLeftEvent');

// Broadcast channel actions (unsequenced)
export const otherUserJoinedWaitingRoomAction = createAction<OtherUserJoinedWaitingRoom>(
  'openNatter:OtherUserJoinedWaitingRoom'
);
export const otherUserJoinedRoomAction = createAction<OtherUserJoinedRoom>('openNatter:OtherUserJoinedRoom');
export const otherUserLeftEventAction = createAction<OtherUserLeftEvent>('openNatter:OtherUserLeftEvent');

/**
 * Message type to action creator mapping
 */
const messageTypeToAction = {
  SelfJoinedWaitingRoom: selfJoinedWaitingRoomAction,
  SelfMatched: selfMatchedAction,
  SelfUnmatched: selfUnmatchedAction,
  SelfLeftEvent: selfLeftEventAction,
  OtherUserJoinedWaitingRoom: otherUserJoinedWaitingRoomAction,
  OtherUserJoinedRoom: otherUserJoinedRoomAction,
  OtherUserLeftEvent: otherUserLeftEventAction,
} as const;

/**
 * Dispatch Open Natter WebSocket messages to Redux actions
 */
export const dispatchOpenNatterMessage = (dispatch: (action: any) => void, message: OpenNatterMessage): void => {
  try {
    // Unwrap SequencedUserNotification to get the actual notification
    let actualMessage: SelfNotification | BroadcastChannelMessage;

    if (message.type === 'SequencedUserNotification') {
      const sequencedMessage = message as SequencedUserNotification;
      actualMessage = sequencedMessage.userNotification;
      logger.debug('[OpenNatterMessageDispatcher] Unwrapped SequencedUserNotification', {
        sequenceId: sequencedMessage.sequenceId,
        actualType: actualMessage.type,
      });
    } else {
      // Already unwrapped or broadcast message
      actualMessage = message;
    }

    const actionCreator = messageTypeToAction[actualMessage.type];

    if (!actionCreator) {
      logger.error('[OpenNatterMessageDispatcher] Unknown message type', {
        type: actualMessage.type,
      });
      return;
    }

    dispatch(actionCreator(actualMessage as never));
  } catch (error) {
    logger.error('[OpenNatterMessageDispatcher] Error dispatching message', {
      error,
      messageType: message.type,
    });
  }
};
